import React from "react";
import PropTypes from "prop-types";
import { RichText, Image } from "./base/index";
import Callout from "./Callout";
import RichTextInlineImages from "./RichTextInlineImages";
import { contentProps } from "./propTypes";
import YoutubeVideoLink from "./YoutubeVideoLink";
import Article from "./Article";
import Step from "./Step";
import useConditionEvaluator from "../hooks/useConditionEvaluator";
import { PageStateContext } from "../PageContext";
import { debugLog } from "../utils/index";
import {
  createStatusInfo,
  combineStatusInfo,
  createConditionStatusInfo,
} from "../utils/contentStatusUtils";

const ContentBuilder = entry => {
  const {
    id,
    type,
    content,
    steps = null,
    number,
    handleAnchor = null,
    changedType = null,
    changedData = {},
    title = null,
    isNested = false,
    fullWidth,
  } = entry;
  const { evaluateConditions } = useConditionEvaluator();
  const { pageState } = React.useContext(PageStateContext);
  const { isPreview } = pageState; // now the flag is never undefined

  const pageLayoutType = pageState?.chrome?.pageLayoutType || "letter";
  const shouldFullWidth =
    fullWidth || (pageLayoutType === "QSG" && fullWidth !== false) || fullWidth;
  const widthOneHalfClassName =
    shouldFullWidth || fullWidth === false
      ? isNested
        ? "w-3/4"
        : "w-full"
      : "w-3/4 sm:w-3/4 md:w-1/2"; // tailwind sets sm value when screens width is over sm value, and sets md when width over md (confusing)
  const widthOneThirdClassName = shouldFullWidth ? "w-1/2" : "w-1/3";
  const widthOneFourthClassName = shouldFullWidth ? "w-1/3" : "w-1/4";
  const widthOneFifthClassName =
    shouldFullWidth || fullWidth === false ? "w-1/4" : "w-1/5";
  debugLog(1, "ContentBuilder pageLayoutType", pageLayoutType);

  // Guard to ensure content is valid
  if (!content || typeof content !== "object") {
    // We can optionally debugLog or just return null
    debugLog(
      1,
      `ContentBuilder: No valid content for type=${type}. Possibly nested incorrectly.`
    );
    debugLog(1, entry);
    return null;
  }
  // Evaluate conditions for the current content block
  const { shouldHide, conditionSummary } = evaluateConditions(content);

  if (!type) {
    type = content.type;
  }
  debugLog(
    1,
    `ContentBuilder - ID: ${id}, Type: ${type}, isPreview: ${isPreview}, shouldHide: ${shouldHide}, Conditions: ${conditionSummary}`
  );
  debugLog(1, "ContentBuilder content", content);

  // Determine if conditions are set to apply border
  const conditionsExist = conditionSummary !== "None";

  // Set changedType to true if conditions exist
  const blockChangedType = conditionsExist ? 1 : changedType;

  // Create status info objects
  const conditionStatusInfo = createConditionStatusInfo(
    shouldHide,
    conditionSummary
  );
  const changeStatusInfo = createStatusInfo(
    changedType,
    changedData ? `Changed: ${Object.keys(changedData).join(", ")}` : ""
  );

  // Get combined status info that includes both condition and change information
  const statusInfo = combineStatusInfo(conditionStatusInfo, changeStatusInfo);

  // Determine wrapper classes
  const wrapperClassNames = [
    pageState.isPreview && (conditionsExist || changeStatusInfo.status)
      ? "edited-error tooltip"
      : "",
    shouldHide && pageState.isPreview ? "semi-transparent-red-bg" : "",
  ]
    .filter(Boolean)
    .join(" ");

  // Early return if block should be hidden and not in preview
  if (shouldHide && !isPreview) {
    // Add debugLog here with conditionSummary
    debugLog(
      1,
      `ContentBuilder Hiding Block - ID: ${content.id ||
        "N/A"}, Title: ${content.title ||
        "N/A"}, Conditions: ${conditionSummary}`
    );
    return null;
  }

  // If shouldHide but in preview mode, render with semi-transparent background
  // If conditions are set but not fully evaluable, log the conditionSummary
  if (shouldHide && isPreview) {
    debugLog(
      1,
      `ContentBuilder Block may be hidden in normal mode - ID: ${content.id ||
        "N/A"}, Title: ${content.title ||
        "N/A"}, Conditions: ${conditionSummary}`
    );
  } else if (conditionsExist && !shouldHide) {
    // Conditions are set but not fully evaluable or did not trigger hide
    debugLog(
      1,
      `ContentBuilder Conditions set for Block - ID: ${content.id ||
        "N/A"}, Title: ${content.title ||
        "N/A"}, Conditions: ${conditionSummary}`
    );
  }

  // Handle steps (group title and first step to avoid break-inside-avoid for the first step)
  if (steps) {
    return (
      <div className={isNested ? "ml-4" : ""}>
        {/* Wrap only the title and first step in a break-inside-avoid container */}
        {steps.length > 0 && (
          <div className="break-inside-avoid step-1">
            {title && !isNested && (
              <h3 className="text-gray-2 mb-3 mt-10">{title}</h3>
            )}
            <Step
              key={steps[0].id}
              number={1}
              total={steps.length}
              handleAnchor={handleAnchor}
              isNested
              {...steps[0]}
              fullWidth={shouldFullWidth}
              className="first-step"
            />
          </div>
        )}

        {/* Render remaining steps normally */}
        {steps.length > 1 &&
          steps.slice(1).map((item, idx) => (
            <Step
              key={item.id}
              number={idx + 2} // +2 because we're starting from the second item
              total={steps.length}
              handleAnchor={handleAnchor}
              isNested
              {...item}
              fullWidth={shouldFullWidth}
            />
          ))}
      </div>
    );
  }

  // Handle nested content (entries with content array)
  if (content && Array.isArray(content)) {
    return (
      <div className={isNested ? "ml-4" : ""}>
        {title && !isNested && (
          <h3
            className={`${
              shouldFullWidth ? "text-blue-accent" : "text-blue-accent"
            } mb-3 mt-10 break-after-avoid`}
          >
            {title}
          </h3>
        )}
        <Article content={content} handleAnchor={handleAnchor} />
      </div>
    );
  }

  // Handle individual content blocks with conditional rendering
  switch (type) {
    case "textBlock":
      const breakAfter = content?.pageBreakAfter === true;
      return (
        <>
          <div
            className={`${wrapperClassNames} ${id} ${
              shouldFullWidth ? "mt-3" : ""
            }`}
          >
            <RichText
              html={
                statusInfo.status && changedData && changedData.entryContent
                  ? changedData.entryContent
                  : content.paragraph
              }
              handleAnchor={handleAnchor}
              className={`orphans-5 widows-5 text-block ${
                shouldFullWidth ? "mt-4 mb-0" : "mt-4 mb-4"
              }`}
              variant={content.contentType === "legal" ? "sm" : null}
              fullWidth={shouldFullWidth}
              isNested={isNested}
            />

            {statusInfo.status && (
              <span className="tooltiptext">{statusInfo.msg}</span>
            )}
          </div>

          {breakAfter && (
            <div
              /*  visible only to paged.js / print  */
              className="page-break"
              style={{ breakBefore: "always", pageBreakBefore: "always" }}
            />
          )}
        </>
      );

    case "largeImageBlock":
      return (
        <div className={wrapperClassNames}>
          <Image
            image={content.image}
            className={`${shouldFullWidth ? "mt-4 mb-0" : "my-4"} ${
              fullWidth ? "fullWidth" : ""
            } break-before-avoid`}
            fullWidth={shouldFullWidth}
          />
          {statusInfo.status && (
            <span className="tooltiptext">{statusInfo.msg}</span>
          )}
        </div>
      );

    case "fullWidthImageBlock":
      return (
        <div className={wrapperClassNames}>
          <Image image={content.image} className="w-full my-4" fullWidth />
          {statusInfo.status && (
            <span className="tooltiptext">{statusInfo.msg}</span>
          )}
        </div>
      );

    case "textPlusImageBlock":
      return (
        <div
          className={
            statusInfo.status
              ? `edited-error tooltip flex items-center ${widthOneHalfClassName} mt-4 mb-4`
              : `flex items-center break-inside-avoid ${widthOneHalfClassName} ${
                  shouldFullWidth ? "mt-3 mb-0" : "mt-4 mb-4"
                }`
          }
        >
          <RichText
            html={
              statusInfo.status && changedData && changedData.entryContent
                ? changedData.entryContent
                : content.paragraph
            }
            handleAnchor={handleAnchor}
            className="flex-1 orphans-5 widows-5 text-plus-image-block"
            fullWidth={shouldFullWidth}
          />
          <div className={`ml-4 ${widthOneFifthClassName}`}>
            <Image
              image={content.image}
              className="justify-left"
              imageNextToText
              fullWidth={shouldFullWidth}
            />
          </div>
          {statusInfo.status && (
            <span className="tooltiptext">{statusInfo.msg}</span>
          )}
        </div>
      );

    /* ──────────────────────────────
     * Inline-images (CKEditor) block
     * ────────────────────────────── */
    case "richTextInlineImagesBlock":
      return (
        <div
          className={`${wrapperClassNames} ${
            shouldFullWidth ? "mt-4 mb-0" : "mt-4 mb-4"
          }`}
        >
          <RichTextInlineImages
            html={
              statusInfo.status && changedData && changedData.entryContent
                ? changedData.entryContent
                : content.paragraph
            }
            handleAnchor={handleAnchor}
            fullWidth={shouldFullWidth}
            isNested={isNested}
          />
          {statusInfo.status && (
            <span className="tooltiptext">{statusInfo.msg}</span>
          )}
        </div>
      );
    case "calloutBlock":
      return (
        <div className={wrapperClassNames}>
          {statusInfo.status && changedData ? (
            <Callout
              type={
                changedData.calloutType ||
                content.callout?.type ||
                content.calloutType
                  ? content.calloutType
                  : null
              }
              text={
                changedData.entryContent ||
                changedData.calloutText ||
                content.callout?.text ||
                content.calloutText
                  ? content.calloutText
                  : null
              }
              className="my-4 callout-block"
              fullWidth={shouldFullWidth}
            />
          ) : (
            <Callout
              {...content.callout}
              className="my-4 callout-block"
              fullWidth={shouldFullWidth}
            />
          )}
          {statusInfo.status && (
            <span className="tooltiptext">{statusInfo.msg}</span>
          )}
        </div>
      );

    case "videoBlock":
      return (
        <div className={`${wrapperClassNames} break-inside-avoid`}>
          <YoutubeVideoLink
            url={
              statusInfo.status && changedData && changedData.youtubeVideo
                ? changedData.youtubeVideo
                : content.youtubeVideo
            }
            paragraph={
              statusInfo.status && changedData && changedData.entryContent
                ? changedData.entryContent
                : content.paragraph
            }
            className="my-4 youtube-block"
            handleAnchor={handleAnchor}
          />
          {statusInfo.status && (
            <span className="tooltiptext">{statusInfo.msg}</span>
          )}
        </div>
      );

    /**
     * Handle multi-column blocks where data is in `content.columnBlockComposableEntries`
     */
    /* ───────────────────────────────────────────────
     *  MULTI-COLUMN COMPOSABLE BLOCK
     * ─────────────────────────────────────────────── */
    case "columnComposableBlock": {
      // ── 0. Guard ───────────────────────────────
      const columnItems = content.columnBlockComposableEntries || [];
      if (columnItems.length === 0) return null;

      /* 1️⃣  Filter out columns hidden by their own conditions
             (still render them in Preview mode)                   */
      const visibleColumns = columnItems.filter(colItem => {
        const { shouldHideCol } = evaluateConditions(colItem);
        return !(shouldHideCol && !isPreview);
      });
      if (visibleColumns.length === 0) return null;

      /* 2️⃣  SINGLE visible column  →  render it like a normal block
             - no column-block wrapper, no flex / px-2              */
      if (visibleColumns.length === 1) {
        const sole = visibleColumns[0];

        return (
          <div>
            <ContentBuilder
              {...sole}
              handleAnchor={handleAnchor}
              fullWidth={shouldFullWidth}
              isNested
            />
            {statusInfo.status && (
              <span className="tooltiptext">{statusInfo.msg}</span>
            )}
          </div>
        );
      }

      /* 3️⃣  MULTI column  →  original flex layout                   */
      return (
        <div className="overflow-x-auto">
          {" "}
          {/* allow horz scroll aka override parent max-w-content */}
          <div
            className={`column-block flex flex-wrap max-w-contentmaxmargin ${
              shouldFullWidth ? "mb-0" : "mb-4"
            } ${wrapperClassNames}`}
          >
            {visibleColumns.map((colItem, idx) => (
              <div
                key={colItem.id}
                className={`flex-1 px-2 ${shouldFullWidth ? "mb-0" : "mb-4"}`}
              >
                <ContentBuilder
                  key={colItem.id || idx}
                  number={idx}
                  {...colItem}
                  handleAnchor={handleAnchor}
                  fullWidth={shouldFullWidth}
                  isNested
                />
              </div>
            ))}

            {statusInfo.status && (
              <span className="tooltiptext">{statusInfo.msg}</span>
            )}
          </div>
        </div>
      );
    }

    default:
      console.warn(`Unhandled content type: ${type}`);
      return null;
  }
};

ContentBuilder.propTypes = {
  type: PropTypes.string.isRequired,
  content: contentProps,
  steps: PropTypes.arrayOf(PropTypes.object),
  handleAnchor: PropTypes.func,
  changedType: PropTypes.number,
  changedData: PropTypes.object,
  title: PropTypes.string,
  isNested: PropTypes.bool,
  fullWidth: PropTypes.bool,
};

export default ContentBuilder;
